import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Users, RefreshCw, Plus, Calendar, Mail, CheckCircle2, AlertTriangle, Clock4, Trash2, Edit3, Sparkles, Zap, X, TrendingUp } from "lucide-react";
import CustomerPicker from "./CustomerPicker";
import ProposalsHealthPanel from "./ProposalsHealthPanel";

const WEEKDAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const WEEKDAYS_FULL = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const CADENCE_OPTIONS = [
  { k: "weekly", label: "Settimanale" },
  { k: "biweekly", label: "Ogni 2 settimane" },
  { k: "monthly", label: "Mensile" },
  { k: "custom", label: "Personalizzata" },
];

const STATUS_STYLES = {
  ok: { bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-900/50", text: "text-emerald-800 dark:text-emerald-300", dot: "bg-emerald-500", label: "OK" },
  due: { bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-200 dark:border-amber-900/50", text: "text-amber-800 dark:text-amber-300", dot: "bg-amber-500", label: "In scadenza" },
  late: { bg: "bg-rose-50 dark:bg-rose-950/30", border: "border-rose-200 dark:border-rose-900/50", text: "text-rose-800 dark:text-rose-300", dot: "bg-rose-500", label: "In ritardo" },
  new: { bg: "bg-zinc-50 dark:bg-zinc-800/60", border: "border-zinc-200 dark:border-zinc-700", text: "text-zinc-700 dark:text-zinc-300", dot: "bg-zinc-400", label: "Mai prenotato" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const Kpi = ({ label, value, sub, icon: Icon, accent }) => {
  const ring = {
    blue: "from-blue-500/10 to-blue-500/0 ring-blue-500/20", emerald: "from-emerald-500/10 to-emerald-500/0 ring-emerald-500/20",
    rose: "from-rose-500/10 to-rose-500/0 ring-rose-500/20", amber: "from-amber-500/10 to-amber-500/0 ring-amber-500/20",
  }[accent];
  const text = { blue: "text-blue-600", emerald: "text-emerald-600", rose: "text-rose-600", amber: "text-amber-600" }[accent];
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${ring} ring-1 p-5`}>
      <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400"><Icon className={`h-3.5 w-3.5 ${text}`} /> {label}</div>
      <div className="font-display text-3xl mt-2 tabular-nums dark:text-zinc-100">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">{sub}</div>}
    </div>
  );
};

export default function RegularsPanel({ businessId }) {
  const [regulars, setRegulars] = useState([]);
  const [detected, setDetected] = useState([]);
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");
  const [toast, setToast] = useState("");
  const [editing, setEditing] = useState(null); // regular obj or {new: true}
  const [pickingFor, setPickingFor] = useState(null); // regular for which we open date picker
  const [confirmRebook, setConfirmRebook] = useState(null); // recent visit confirmation
  const [filter, setFilter] = useState("all");

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 2800); }

  async function loadAll() {
    setLoading(true);
    try {
      const [r, d, s, biz, cust] = await Promise.all([
        api.get(`/businesses/${businessId}/regulars`),
        api.get(`/businesses/${businessId}/regulars/auto-detect`),
        api.get(`/businesses/${businessId}/regulars/stats`),
        api.get(`/businesses/${businessId}`),
        api.get(`/businesses/${businessId}/customers`),
      ]);
      setRegulars(r.data || []);
      setDetected(d.data || []);
      setStats(s.data);
      setServices(biz.data?.services || []);
      setCustomers((cust.data || []).filter((c) => c.customer_id && !c.customer_id.startsWith("walkin-") && !c.customer_id.startsWith("guest-") && !c.customer_id.startsWith("deleted-")));
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (businessId) loadAll(); /* eslint-disable-next-line */ }, [businessId]);

  const filtered = useMemo(() => {
    if (filter === "all") return regulars;
    return regulars.filter((r) => r.status === filter);
  }, [regulars, filter]);

  async function bookNow(reg, customStart) {
    setBusyKey(`book-${reg.id}`);
    try {
      const payload = customStart ? { start_at: customStart } : (reg.suggested_slot ? { start_at: reg.suggested_slot } : {});
      const { data } = await api.post(`/regulars/${reg.id}/book-now`, payload);
      showToast(`Prenotato per ${reg.customer_name} ✓`);
      loadAll();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    } finally { setBusyKey(""); }
  }

  async function sendProposal(reg) {
    setBusyKey(`prop-${reg.id}`);
    try {
      await api.post(`/regulars/${reg.id}/send-proposal`);
      showToast("Proposta inviata ✓");
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    } finally { setBusyKey(""); }
  }

  async function toggleAuto(reg) {
    setBusyKey(`auto-${reg.id}`);
    try {
      await api.patch(`/regulars/${reg.id}`, { auto_book: !reg.auto_book });
      showToast(!reg.auto_book ? "Auto-prenotazione attivata" : "Auto-prenotazione disattivata");
      loadAll();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    } finally { setBusyKey(""); }
  }

  async function removeRegular(reg) {
    setBusyKey(`del-${reg.id}`);
    try {
      await api.delete(`/regulars/${reg.id}`);
      showToast("Cliente rimosso");
      setRegulars((p) => p.filter((r) => r.id !== reg.id));
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    } finally { setBusyKey(""); }
  }

  async function redeemLoyalty(reg) {
    setBusyKey(`redeem-${reg.id}`);
    try {
      await api.post(`/regulars/${reg.id}/loyalty/redeem`);
      showToast(`🎁 Premio riscattato per ${reg.customer_name} ✓`);
      loadAll();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    } finally { setBusyKey(""); }
  }

  async function markFromDetected(d) {
    setBusyKey(`mark-${d.customer_id}-${d.service_id}`);
    try {
      await api.post(`/businesses/${businessId}/regulars`, {
        customer_id: d.customer_id,
        service_id: d.service_id,
        cadence: d.cadence,
        preferred_dow: d.preferred_dow,
        preferred_hour: d.preferred_hour,
        preferred_staff_id: d.preferred_staff_id,
        auto_book: false,
        enabled: true,
      });
      showToast(`${d.customer_name} marcato come abituale ✓`);
      loadAll();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    } finally { setBusyKey(""); }
  }

  return (
    <div className="space-y-6" data-testid="regulars-panel">
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-violet-600">
          <Users className="h-4 w-4" /> Clienti Abituali
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap mt-1">
          <h2 className="font-display text-2xl md:text-3xl font-medium dark:text-zinc-100">I tuoi affezionati</h2>
          <div className="flex gap-2">
            <button onClick={() => setEditing({ new: true })} className="h-9 px-4 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium inline-flex items-center gap-2" data-testid="regulars-add">
              <Plus className="h-4 w-4" /> Aggiungi
            </button>
            <button onClick={loadAll} className="h-9 px-4 rounded-full bg-zinc-900 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-zinc-800" data-testid="regulars-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
            </button>
          </div>
        </div>
        <p className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">Prenotoo si ricorda dei tuoi clienti e ti avvisa quando dovrebbero tornare.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="regulars-kpis">
        <Kpi label="Clienti abituali" value={stats?.total_regulars ?? "—"} icon={Users} accent="blue" />
        <Kpi label="In ritardo" value={stats?.late_count ?? "—"} sub={`${stats?.due_count ?? 0} in scadenza`} icon={AlertTriangle} accent="rose" />
        <Kpi label="Auto-prenotati" value={stats?.auto_booked_in_window ?? "—"} sub={`Ultimi ${stats?.window_days ?? 30}g`} icon={Zap} accent="amber" />
        <Kpi label="Fatturato abituali" value={`€${(stats?.revenue_from_regulars ?? 0).toFixed(0)}`} sub={`Ultimi ${stats?.window_days ?? 30}g`} icon={TrendingUp} accent="emerald" />
      </div>

      {/* Health proposte — visible only if at least one proposal has been sent */}
      <ProposalsHealthPanel businessId={businessId} />

      {/* Filter pills */}
      <div className="flex gap-2 flex-wrap" data-testid="regulars-filters">
        {[
          { k: "all", label: `Tutti (${regulars.length})` },
          { k: "late", label: `In ritardo (${regulars.filter((r) => r.status === "late").length})` },
          { k: "due", label: `In scadenza (${regulars.filter((r) => r.status === "due").length})` },
          { k: "ok", label: `OK (${regulars.filter((r) => r.status === "ok").length})` },
          { k: "new", label: `Nuovi (${regulars.filter((r) => r.status === "new").length})` },
        ].map((o) => (
          <button key={o.k} onClick={() => setFilter(o.k)}
            className={`h-8 px-3 rounded-full text-xs font-medium border ${filter === o.k ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"}`}
            data-testid={`filter-${o.k}`}>
            {o.label}
          </button>
        ))}
      </div>

      {/* Regulars list */}
      {loading ? (
        <div className="text-sm text-zinc-400 dark:text-zinc-500">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl bg-zinc-50 border border-dashed border-zinc-200 px-6 py-10 text-center dark:bg-zinc-800 dark:border-zinc-700" data-testid="regulars-empty">
          <Users className="h-8 w-8 text-zinc-400 mx-auto mb-2 dark:text-zinc-500" />
          <p className="text-sm text-zinc-500 dark:text-zinc-400">{regulars.length === 0 ? "Nessun cliente abituale ancora." : "Nessun cliente in questa categoria."}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {filtered.map((r) => (
            <RegularCard
              key={r.id}
              r={r}
              busyKey={busyKey}
              onEdit={() => setEditing(r)}
              onBookNow={() => {
                if (r.days_since_last !== null && r.days_since_last !== undefined && r.days_since_last < 3) {
                  setConfirmRebook(r);
                } else if (r.suggested_slot) {
                  bookNow(r);
                } else {
                  setPickingFor(r);
                }
              }}
              onPickSlot={() => setPickingFor(r)}
              onProposal={() => sendProposal(r)}
              onToggleAuto={() => toggleAuto(r)}
              onDelete={() => removeRegular(r)}
              onRedeem={() => redeemLoyalty(r)}
            />
          ))}
        </div>
      )}

      {/* Auto-detected suggestions */}
      {detected.length > 0 && (
        <section data-testid="regulars-detected">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-blue-600" />
            <h3 className="font-display text-lg dark:text-zinc-100">Rilevati automaticamente</h3>
            <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">({detected.length})</span>
          </div>
          <p className="text-sm text-zinc-500 mb-3 dark:text-zinc-400">Clienti che si comportano già da abituali, ma non sono ancora marcati.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {detected.map((d) => (
              <div key={`${d.customer_id}-${d.service_id}`} className="rounded-2xl bg-white ring-1 ring-zinc-100 p-4 flex items-center justify-between gap-3 dark:bg-zinc-900 dark:ring-zinc-800" data-testid={`detected-${d.customer_id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate dark:text-zinc-100">{d.customer_name}</div>
                  <div className="text-xs text-zinc-500 truncate dark:text-zinc-400">{d.service_name} · ogni ~{Math.round(d.avg_gap_days)}g · {d.total_visits} visite</div>
                </div>
                <button onClick={() => markFromDetected(d)} disabled={busyKey === `mark-${d.customer_id}-${d.service_id}`}
                  className="h-9 px-3 rounded-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-xs font-medium whitespace-nowrap"
                  data-testid={`mark-regular-${d.customer_id}`}>
                  + Abituale
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {confirmRebook && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center p-4" onClick={() => setConfirmRebook(null)} data-testid="confirm-rebook">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6 card-soft dark:bg-zinc-900">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-amber-600">Visita recente</div>
            <h2 className="font-display text-2xl font-medium mt-1 mb-3 dark:text-zinc-100">Sicuro di voler prenotare di nuovo?</h2>
            <p className="text-sm text-zinc-600 mb-4 dark:text-zinc-400">
              <strong>{confirmRebook.customer_name}</strong> ha già fatto <strong>{confirmRebook.service_name}</strong> {confirmRebook.days_since_last} {confirmRebook.days_since_last === 1 ? "giorno" : "giorni"} fa.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmRebook(null)} className="btn-secondary flex-1" data-testid="confirm-rebook-cancel">Annulla</button>
              <button
                onClick={() => {
                  const reg = confirmRebook;
                  setConfirmRebook(null);
                  if (reg.suggested_slot) bookNow(reg); else setPickingFor(reg);
                }}
                className="flex-1 h-11 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold"
                data-testid="confirm-rebook-yes"
              >
                Sì, prenota comunque
              </button>
            </div>
          </div>
        </div>
      )}

      {pickingFor && (
        <DatePickerModal
          regular={pickingFor}
          onCancel={() => setPickingFor(null)}
          onConfirm={(iso) => {
            const reg = pickingFor;
            setPickingFor(null);
            bookNow(reg, iso);
          }}
        />
      )}

      {editing && (
        <RegularEditor
          businessId={businessId}
          regular={editing.new ? null : editing}
          services={services}
          customers={customers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAll(); showToast("Salvato ✓"); }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-zinc-900 text-white text-sm px-4 py-2.5 rounded-full shadow-lg" data-testid="regulars-toast">{toast}</div>
      )}
    </div>
  );
}

function RegularCard({ r, busyKey, onEdit, onBookNow, onPickSlot, onProposal, onToggleAuto, onDelete, onRedeem }) {
  const st = STATUS_STYLES[r.status] || STATUS_STYLES.new;
  const dowLabel = r.preferred_dow !== null && r.preferred_dow !== undefined ? WEEKDAYS_FULL[r.preferred_dow] : null;
  const cadenceLabel = r.cadence === "custom" ? `ogni ${r.custom_gap_days || 30}g` : { weekly: "settimanale", biweekly: "ogni 2 sett.", monthly: "mensile" }[r.cadence];
  const overdueText = r.status === "late" ? `In ritardo di ${r.days_overdue}g` : r.status === "due" ? `Da prenotare entro pochi giorni` : r.status === "ok" ? (r.next_booking_start ? `Prossimo: ${fmtDateTime(r.next_booking_start)}` : "Aggiornato di recente") : "Mai prenotato";
  const k = r.id;
  const propBusy = busyKey === `prop-${k}`, bookBusy = busyKey === `book-${k}`, autoBusy = busyKey === `auto-${k}`, delBusy = busyKey === `del-${k}`, redeemBusy = busyKey === `redeem-${k}`;
  const alreadyBooked = !!r.next_booking_id;
  const bookLabel = alreadyBooked ? "Già prenotato" : (r.suggested_slot ? "Prenota subito" : "Scegli orario");
  const loy = r.loyalty;

  return (
    <div className={`rounded-2xl bg-white ring-1 ring-zinc-100 overflow-hidden dark:bg-zinc-900 dark:ring-zinc-800`} data-testid={`regular-card-${r.id}`}>
      <div className={`${st.bg} ${st.border} border-b px-4 py-2.5 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${st.dot}`} />
          <span className={`text-[10px] font-semibold tracking-[0.15em] uppercase ${st.text}`}>{st.label}</span>
        </div>
        {r.auto_book && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full dark:bg-amber-950/40 dark:text-amber-300">
            <Zap className="h-3 w-3" /> Auto
          </span>
        )}
      </div>
      <div className="p-4 space-y-2.5">
        <div>
          <div className="font-display text-lg leading-tight dark:text-zinc-100">{r.customer_name}</div>
          <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">{r.service_name} · {cadenceLabel} · €{(r.service_price ?? 0).toFixed(0)}</div>
        </div>
        <div className="rounded-xl bg-zinc-50 p-3 text-xs space-y-1 dark:bg-zinc-800">
          {dowLabel && (
            <div><Clock4 className="inline h-3 w-3 mr-1 text-zinc-400 dark:text-zinc-500" /> Preferito: <strong className="text-zinc-800 dark:text-zinc-200">{dowLabel}</strong>{r.preferred_hour !== null ? ` ${String(r.preferred_hour).padStart(2, "0")}:${String(r.preferred_minute || 0).padStart(2, "0")}` : ""}</div>
          )}
          <div>Ultimo: <strong className="text-zinc-800 dark:text-zinc-200">{r.last_visit ? `${fmtDateTime(r.last_visit)} (${r.days_since_last}g fa)` : "mai"}</strong></div>
          <div className={`font-medium ${st.text}`}>💡 {overdueText}</div>
          {r.suggested_slot && r.status !== "ok" && (
            <div>Proposta: <strong className="text-zinc-800 capitalize dark:text-zinc-200">{fmtDateTime(r.suggested_slot)}</strong></div>
          )}
        </div>

        {/* Proposal stats: shown only if at least one proposal was sent */}
        {r.proposals_sent > 0 && (
          <div className="rounded-xl bg-blue-50 border border-blue-100 px-3 py-2 text-xs space-y-1 dark:bg-blue-950/30 dark:border-blue-900/40" data-testid={`proposal-stats-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-blue-900 inline-flex items-center gap-1.5 dark:text-blue-200">
                <Mail className="h-3 w-3" /> Proposte inviate
              </span>
              <span className="text-blue-900 dark:text-blue-200 tabular-nums">
                <strong>{r.proposals_sent}</strong>
                {r.accept_rate !== null && r.accept_rate !== undefined && (
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${r.accept_rate >= 70 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" : r.accept_rate >= 40 ? "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300" : "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300"}`}>
                    {r.accept_rate}% accettate
                  </span>
                )}
              </span>
            </div>
            <div className="text-[11px] text-blue-800/80 dark:text-blue-300/80">
              {r.proposals_accepted} accettate · {r.proposals_declined} rifiutate
              {r.proposals_sent - r.proposals_accepted - r.proposals_declined > 0 && (
                <> · {r.proposals_sent - r.proposals_accepted - r.proposals_declined} in attesa</>
              )}
            </div>
            {r.last_decline_reason && (
              <div className="text-[11px] text-blue-800/90 italic mt-1 dark:text-blue-300/90" title={r.last_decline_reason}>
                Ultimo rifiuto: "{r.last_decline_reason.length > 70 ? r.last_decline_reason.slice(0, 67) + "…" : r.last_decline_reason}"
              </div>
            )}
          </div>
        )}

        {loy && (
          <div className={`rounded-xl px-3 py-2 ${loy.ready_to_redeem ? "bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900/50" : "bg-violet-50 border border-violet-100 dark:bg-violet-950/30 dark:border-violet-900/50"}`} data-testid={`loyalty-${r.id}`}>            <div className="flex items-center justify-between gap-2">
              <div className="text-xs">
                <div className={`font-semibold ${loy.ready_to_redeem ? "text-emerald-800 dark:text-emerald-300" : "text-violet-800 dark:text-violet-300"}`}>
                  🎁 {loy.ready_to_redeem ? "Pronto per il premio!" : `${loy.current}/${loy.threshold} visite`}
                </div>
                <div className={`mt-0.5 ${loy.ready_to_redeem ? "text-emerald-700 dark:text-emerald-400/90" : "text-violet-700/80 dark:text-violet-400/80"}`}>{loy.reward_text}</div>
              </div>
              {loy.ready_to_redeem && (
                <button
                  onClick={onRedeem}
                  disabled={redeemBusy}
                  className="h-8 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold whitespace-nowrap"
                  data-testid={`redeem-${r.id}`}
                >
                  {redeemBusy ? "…" : "Riscatta"}
                </button>
              )}
            </div>
            {!loy.ready_to_redeem && (
              <div className="mt-1.5 h-1.5 rounded-full bg-violet-100 overflow-hidden dark:bg-violet-950/50">
                <div className="h-full bg-violet-600 transition-all" style={{ width: `${Math.min(100, (loy.current / loy.threshold) * 100)}%` }} />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={onBookNow}
            disabled={bookBusy || alreadyBooked}
            title={alreadyBooked ? `Già prenotato il ${fmtDateTime(r.next_booking_start)}` : ""}
            className="flex-1 h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium inline-flex items-center justify-center gap-1.5"
            data-testid={`btn-book-${r.id}`}
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> {bookBusy ? "…" : bookLabel}
          </button>
          {!alreadyBooked && r.suggested_slot && (
            <button onClick={onPickSlot} title="Scegli un altro orario" className="h-9 px-3 rounded-full bg-white border border-zinc-200 hover:border-zinc-400 text-zinc-700 text-xs font-medium dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-300" data-testid={`btn-pick-${r.id}`}>
              <Calendar className="h-3.5 w-3.5" />
            </button>
          )}
          <button onClick={onProposal} disabled={propBusy || !r.customer_email || !r.suggested_slot}
            title={!r.customer_email ? "Cliente senza email" : (!r.suggested_slot ? "Configura giorno/ora preferiti" : "")}
            className="h-9 px-3 rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-medium inline-flex items-center gap-1.5"
            data-testid={`btn-proposal-${r.id}`}>
            <Mail className="h-3.5 w-3.5" /> Proposta
          </button>
        </div>
        <div className="flex gap-1 pt-2 border-t border-zinc-100 dark:border-zinc-800">
          <button onClick={onToggleAuto} disabled={autoBusy} className={`flex-1 h-8 rounded-full text-xs font-medium border ${r.auto_book ? "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-900/50" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"}`}
            data-testid={`toggle-auto-${r.id}`}>
            <Zap className="inline h-3 w-3 mr-1" />{r.auto_book ? "Auto: ON" : "Auto: OFF"}
          </button>
          <button onClick={onEdit} className="h-8 px-3 rounded-full bg-white border border-zinc-200 hover:border-zinc-400 text-zinc-700 text-xs font-medium dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-300" data-testid={`edit-${r.id}`}>
            <Edit3 className="h-3 w-3" />
          </button>
          <button onClick={onDelete} disabled={delBusy} className="h-8 px-3 rounded-full bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 text-xs font-medium dark:bg-zinc-900 dark:border-rose-800/50" data-testid={`delete-${r.id}`}>
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function DatePickerModal({ regular, onCancel, onConfirm }) {
  // Pre-fill with suggested_slot if any, else next preferred dow/hour from now, else now+1h
  const initial = (() => {
    const base = regular.suggested_slot ? new Date(regular.suggested_slot) : (() => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(regular.preferred_hour ?? 10, regular.preferred_minute || 0, 0, 0);
      return d;
    })();
    const p = (n) => String(n).padStart(2, "0");
    return `${base.getFullYear()}-${p(base.getMonth() + 1)}-${p(base.getDate())}T${p(base.getHours())}:${p(base.getMinutes())}`;
  })();
  const [value, setValue] = useState(initial);
  const [err, setErr] = useState("");

  function toLocalIso(s) {
    const d = new Date(s);
    const pad = (n) => String(n).padStart(2, "0");
    const tz = -d.getTimezoneOffset();
    const sign = tz >= 0 ? "+" : "-";
    const tzH = pad(Math.floor(Math.abs(tz) / 60));
    const tzM = pad(Math.abs(tz) % 60);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${tzH}:${tzM}`;
  }

  function submit() {
    setErr("");
    if (!value) { setErr("Scegli data e orario"); return; }
    if (new Date(value) <= new Date()) { setErr("Scegli un orario futuro"); return; }
    onConfirm(toLocalIso(value));
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center p-4" onClick={onCancel} data-testid="regular-date-picker">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6 card-soft dark:bg-zinc-900">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-emerald-700 dark:text-emerald-300">Prenota appuntamento</div>
        <h2 className="font-display text-2xl font-medium mt-1 mb-1 dark:text-zinc-100">{regular.customer_name}</h2>
        <div className="text-sm text-zinc-500 mb-4 dark:text-zinc-400">{regular.service_name} · €{(regular.service_price ?? 0).toFixed(0)}</div>

        <label className="block mb-3">
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Data e ora</span>
          <input type="datetime-local" step="900" value={value} onChange={(e) => setValue(e.target.value)}
            className="mt-1 w-full h-12 rounded-xl border border-zinc-300 px-3 text-sm focus:border-emerald-500 focus:outline-none dark:border-zinc-600"
            data-testid="picker-input" />
        </label>

        {err && <div className="text-sm text-rose-600 mb-3" data-testid="picker-error">{err}</div>}

        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-secondary flex-1" data-testid="picker-cancel">Annulla</button>
          <button onClick={submit} className="flex-1 h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold" data-testid="picker-confirm">
            Prenota
          </button>
        </div>
      </div>
    </div>
  );
}

function RegularEditor({ businessId, regular, services, customers, onClose, onSaved }) {
  const isNew = !regular;
  const [picked, setPicked] = useState(
    regular ? { kind: "user", id: regular.customer_id, name: regular.customer_name, email: regular.customer_email, phone: null, contactId: null } : null
  );
  const [form, setForm] = useState({
    service_id: regular?.service_id || "",
    cadence: regular?.cadence || "monthly",
    custom_gap_days: regular?.custom_gap_days || 30,
    preferred_dow: regular?.preferred_dow ?? "",
    preferred_hour: regular?.preferred_hour ?? "",
    preferred_minute: regular?.preferred_minute || 0,
    auto_book: regular?.auto_book || false,
    enabled: regular?.enabled ?? true,
    loyalty_enabled: regular?.loyalty_enabled || false,
    loyalty_threshold: regular?.loyalty_threshold || 10,
    loyalty_reward_text: regular?.loyalty_reward_text || "Sconto 10% sulla prossima prenotazione",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setErr("");
    if (!form.service_id) { setErr("Seleziona un servizio"); return; }
    if (isNew && !picked) { setErr("Seleziona un cliente"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        preferred_dow: form.preferred_dow === "" ? null : Number(form.preferred_dow),
        preferred_hour: form.preferred_hour === "" ? null : Number(form.preferred_hour),
        preferred_minute: Number(form.preferred_minute) || 0,
        custom_gap_days: form.cadence === "custom" ? Number(form.custom_gap_days) : null,
      };
      if (isNew) {
        if (picked.kind === "contact") {
          payload.contact_id = picked.contactId;
        } else {
          payload.customer_id = picked.id;
        }
        await api.post(`/businesses/${businessId}/regulars`, payload);
      } else {
        await api.patch(`/regulars/${regular.id}`, payload);
      }
      onSaved();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Errore");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="regular-editor">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6 card-soft max-h-[92vh] overflow-y-auto dark:bg-zinc-900">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-violet-600">{isNew ? "Nuovo cliente abituale" : "Modifica abituale"}</div>
        <h2 className="font-display text-2xl font-medium mt-1 mb-4 dark:text-zinc-100">{isNew ? "Aggiungi" : regular.customer_name}</h2>

        {isNew && (
          <div className="mb-3">
            <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 block mb-1 dark:text-zinc-400">Cliente</span>
            <CustomerPicker
              businessId={businessId}
              value={picked}
              onChange={setPicked}
              allowWalkin={false}
              allowCreate={true}
              placeholder="Cerca cliente o aggiungi un contatto…"
            />
          </div>
        )}

        <label className="block mb-3">
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Servizio</span>
          <select value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}
            className="mt-1 w-full h-12 rounded-xl border border-zinc-300 px-3 text-sm focus:border-violet-500 focus:outline-none dark:border-zinc-600"
            data-testid="editor-service">
            <option value="">— Seleziona —</option>
            {services.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.duration_min}min · €{s.price}</option>)}
          </select>
        </label>

        <label className="block mb-3">
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Cadenza</span>
          <div className="mt-1 grid grid-cols-2 gap-2">
            {CADENCE_OPTIONS.map((o) => (
              <button key={o.k} type="button" onClick={() => setForm({ ...form, cadence: o.k })}
                className={`h-10 rounded-xl text-xs font-medium border ${form.cadence === o.k ? "bg-violet-600 text-white border-violet-600" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"}`}
                data-testid={`editor-cadence-${o.k}`}>{o.label}</button>
            ))}
          </div>
          {form.cadence === "custom" && (
            <input type="number" min="2" max="365" value={form.custom_gap_days}
              onChange={(e) => setForm({ ...form, custom_gap_days: e.target.value })}
              placeholder="Giorni tra una visita e l'altra"
              className="mt-2 w-full h-10 rounded-xl border border-zinc-300 px-3 text-sm focus:border-violet-500 focus:outline-none dark:border-zinc-600"
              data-testid="editor-custom-gap" />
          )}
        </label>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <label className="block">
            <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Giorno preferito</span>
            <select value={form.preferred_dow} onChange={(e) => setForm({ ...form, preferred_dow: e.target.value })}
              className="mt-1 w-full h-10 rounded-xl border border-zinc-300 px-3 text-sm dark:border-zinc-600" data-testid="editor-dow">
              <option value="">Nessuno</option>
              {WEEKDAYS_FULL.map((w, i) => <option key={i} value={i}>{w}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Ora preferita</span>
            <select value={form.preferred_hour} onChange={(e) => setForm({ ...form, preferred_hour: e.target.value })}
              className="mt-1 w-full h-10 rounded-xl border border-zinc-300 px-3 text-sm dark:border-zinc-600" data-testid="editor-hour">
              <option value="">Nessuna</option>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
            </select>
          </label>
        </div>

        <label className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100 cursor-pointer mb-3 dark:bg-amber-950/30 dark:border-amber-900/50">
          <input type="checkbox" checked={form.auto_book} onChange={(e) => setForm({ ...form, auto_book: e.target.checked })}
            className="h-5 w-5 accent-amber-600 mt-0.5" data-testid="editor-auto-book" />
          <div className="text-sm">
            <div className="font-semibold text-amber-900 inline-flex items-center gap-1 dark:text-amber-200"><Zap className="h-3.5 w-3.5" /> Prenota automaticamente</div>
            <div className="text-xs text-amber-800 mt-0.5 dark:text-amber-300">Quando il cliente è "in scadenza", il sistema crea l'appuntamento nel giorno/ora preferiti se lo slot è libero.</div>
          </div>
        </label>

        <label className="flex items-start gap-3 p-3 rounded-xl bg-violet-50 border border-violet-100 cursor-pointer mb-4 dark:bg-violet-950/30 dark:border-violet-900/50">
          <input type="checkbox" checked={form.loyalty_enabled} onChange={(e) => setForm({ ...form, loyalty_enabled: e.target.checked })}
            className="h-5 w-5 accent-violet-600 mt-0.5" data-testid="editor-loyalty" />
          <div className="text-sm flex-1 min-w-0">
            <div className="font-semibold text-violet-900 dark:text-violet-200">🎁 Programma fedeltà</div>
            <div className="text-xs text-violet-800 mt-0.5 mb-2 dark:text-violet-300">Premia il cliente dopo un numero di visite.</div>
            {form.loyalty_enabled && (
              <div className="space-y-2 mt-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-violet-900 whitespace-nowrap dark:text-violet-200">Ogni</span>
                  <input type="number" min="2" max="100" value={form.loyalty_threshold}
                    onChange={(e) => setForm({ ...form, loyalty_threshold: Number(e.target.value) || 10 })}
                    className="w-16 h-8 rounded-lg border border-violet-200 px-2 text-sm bg-white dark:bg-zinc-900 dark:border-violet-900/50 dark:text-zinc-100" data-testid="editor-loyalty-threshold" />
                  <span className="text-xs text-violet-900 dark:text-violet-200">visite</span>
                </div>
                <input type="text" value={form.loyalty_reward_text}
                  onChange={(e) => setForm({ ...form, loyalty_reward_text: e.target.value })}
                  placeholder="Es. Sconto 10% / Trattamento omaggio"
                  className="w-full h-9 rounded-lg border border-violet-200 px-2 text-xs bg-white dark:bg-zinc-900 dark:border-violet-900/50 dark:text-zinc-100" data-testid="editor-loyalty-reward" />
              </div>
            )}
          </div>
        </label>

        {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2 mb-3 dark:bg-rose-950/30 dark:border-rose-900/50 dark:text-rose-300" data-testid="editor-error">{err}</div>}

        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1" data-testid="editor-cancel">Annulla</button>
          <button onClick={submit} disabled={busy} className="flex-1 h-11 rounded-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-semibold" data-testid="editor-save">
            {busy ? "Salvo…" : (isNew ? "Aggiungi" : "Salva")}
          </button>
        </div>
      </div>
    </div>
  );
}
